import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Card, CardContent, Typography } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell, Label } from 'recharts';

import formatMoney from '../../../../helpers/formatMoneyHelper';

const COLORS = [
  'rgb(203,100,35)',
  '#111827',
  '#555B63',
  '#A6ABB2',
  '#C9CDD2',
  '#E2E5E9',
  '#BDBDBD',
  '#D6A77A'
];

export default function BookAUMComposition() {
  const { fullBook } = useSelector((state) => state.aum);

  const latestBook = fullBook?.[0];

  const providers = useMemo(() => {
    return (latestBook?.providers || []).map((provider, index) => ({
      ...provider,
      value: provider.amount,
      color: COLORS[index % COLORS.length]
    }));
  }, [latestBook]);

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        borderRadius: 0,
        border: '1px solid #ECECEC',
        boxShadow: '0 2px 12px rgba(0,0,0,.05)',
        height: 260
      }}>
      <CardContent
        sx={{
          p: 2,
          height: '100%',
          '&:last-child': {
            pb: 2
          }
        }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 12,
            mb: 1.5
          }}>
          Book AUM Composition
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          height="calc(100% - 32px)">
          {/* Donut */}
          <Box
            sx={{
              width: '46%',
              height: '100%',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <Pie
                  data={providers}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  outerRadius="80%"
                  innerRadius="56%"
                  paddingAngle={0}
                  stroke="none">
                  {providers.map((entry) => (
                    <Cell key={entry.id} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Center text */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pointerEvents: 'none'
              }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: '#2A3242'
                }}>
                {formatMoney(latestBook?.total ?? 0)}
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 10,
                  color: '#777',
                  lineHeight: 1
                }}>
                Total
              </Typography>
            </Box>
          </Box>

          {/* Legend */}
          <Box
            sx={{
              width: '44%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 1.2,
              minWidth: 0
            }}>
            {providers.map((item) => (
              <Box key={item.id} display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1} minWidth={0} flex={1}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: item.color,
                      flexShrink: 0
                    }}
                  />

                  <Typography
                    noWrap
                    sx={{
                      fontSize: 10,
                      flex: 1
                    }}>
                    {item.name}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    ml: 1,
                    flexShrink: 0
                  }}>
                  {(item.percentage * 100).toFixed(1)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
